import React from "react";
import { IResourceComponentsProps } from "@refinedev/core";
import { List, EditButton, DeleteButton } from "@refinedev/mantine";
import { useTable } from "@refinedev/react-table";
import { ColumnDef, Header } from "@tanstack/react-table";
import { ActionIcon, Badge, Flex, LoadingOverlay, Pagination, ScrollArea, Select, Table } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { CancellationReason } from "entities/cancel_flow";
import { selectBrands, colorsByBrand } from "entities/cancel_flow/brand";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import useCancelFlowPermissions from "./useCancelFlowPermissions";

const activeOptions = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

export const CancellationReasonList: React.FC<IResourceComponentsProps> = () => {
  const { canCreate, canEdit, canDelete } = useCancelFlowPermissions();

  const columns = React.useMemo<ColumnDef<CancellationReason>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
      },
      {
        id: "brand",
        accessorKey: "brand",
        header: "Brand",
        cell: ({ getValue }) => {
          const value = getValue<CancellationReason["brand"]>();
          return (
            <Badge variant="filled" color={colorsByBrand[value]}>
              {value}
            </Badge>
          );
        },
        meta: {
          filterElement: ({ header }: { header: Header<CancellationReason, any> }) => (
            <Select
              placeholder="Brand"
              data={selectBrands}
              onChange={header.column.setFilterValue}
              clearable
            />
          ),
        },
      },
      {
        id: "label",
        accessorKey: "label",
        header: "Label",
        meta: {
          filterOperator: "contains",
        },
      },
      {
        id: "sort_order",
        accessorKey: "sort_order",
        header: "Sort Order",
      },
      {
        id: "allow_custom_text",
        accessorKey: "allow_custom_text",
        header: "Custom Text",
        cell: ({ getValue }) => {
          const value = getValue<boolean>();
          return (
            <Badge variant="filled" color={value ? "blue" : "gray"}>
              {value ? "Yes" : "No"}
            </Badge>
          );
        },
      },
      {
        id: "is_active",
        accessorKey: "is_active",
        header: "Status",
        cell: ({ getValue }) => {
          const value = getValue<boolean>();
          return (
            <Badge variant="filled" color={value ? "green" : "gray"}>
              {value ? "Active" : "Inactive"}
            </Badge>
          );
        },
        meta: {
          filterElement: ({ header }: { header: Header<CancellationReason, any> }) => (
            <Select
              placeholder="Status"
              data={activeOptions}
              onChange={header.column.setFilterValue}
              clearable
            />
          ),
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: function render({ row: { original } }) {
          return (
            <Flex columnGap={4}>
              {canEdit && <EditButton hideText recordItemId={original.id} />}
              {canDelete && <DeleteButton hideText recordItemId={original.id} />}
            </Flex>
          );
        },
        meta: { isPlaceholder: true },
      },
    ],
    [canEdit, canDelete]
  );

  const {
    getHeaderGroups,
    getRowModel,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { isFetching, refetch },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      pagination: { pageSize: 50 },
    },
  });

  return (
    <List
      canCreate={canCreate}
      headerButtons={({ defaultButtons }) => (
        <>
          <ActionIcon variant="subtle" onClick={() => refetch()} loading={isFetching}>
            <IconRefresh size={18} />
          </ActionIcon>
          {defaultButtons}
        </>
      )}
    >
      <AppliedFilters<CancellationReason> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<CancellationReason> headerGroups={getHeaderGroups()} />
          <Rows<CancellationReason> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
